import { MessageBarType } from '@fluentui/react';
import { useNotification } from '@h2oai/ui-kit';
import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { RoutePaths } from '../../pages/RoutePaths';
import { Engine, Engine_State } from '../gen/ai/h2o/engine/v1/engine_pb';

// Global tracking for engines in STARTING state (persists across component unmounts)
const trackedStartingEngines = new Set<string>();
const previousEngineStates = new Map<string, Engine_State>();

interface UseEngineStateMonitorOptions {
  refetchInterval?: number;
}

/**
 * Hook that monitors engine state transitions and shows notifications
 * when engines transition from STARTING to RUNNING or FAILED.
 *
 * Returns functions to track engines and handle state transitions,
 * along with a computed refetch interval that continues polling
 * when there are tracked engines, even after component unmounts.
 */
export const useEngineStateMonitor = (options: UseEngineStateMonitorOptions = {}) => {
  const { refetchInterval } = options;
  const { addNotification } = useNotification();
  const history = useHistory();

  // Detect state transitions and show notifications
  const handleStateTransitions = useCallback(
    (currentEngines: Engine[] | undefined) => {
      if (!currentEngines) return;

      const enginesToRemove: string[] = [];

      trackedStartingEngines.forEach((engineName) => {
        const currentEngine = currentEngines.find((e) => e.name === engineName);
        if (!currentEngine) return;

        const previousState = previousEngineStates.get(engineName);
        const currentState = currentEngine.state as Engine_State;

        const runningStarted = currentState === Engine_State.RUNNING && previousState !== Engine_State.RUNNING;
        const runningFailed = currentState === Engine_State.FAILED && previousState !== Engine_State.FAILED;
        const ignoredEngine =
          previousState !== undefined &&
          [Engine_State.PAUSED, Engine_State.TERMINATED, Engine_State.DELETING].includes(currentState) &&
          previousState !== currentState;

        // Detect transitions to final states
        if (runningStarted) {
          addNotification({
            message: `Engine "${currentEngine.displayName || engineName}" has started successfully and is now running.`,
            messageBarType: MessageBarType.success,
            title: 'Engine Started',
            onClick: () => {
              history.push(RoutePaths.MY_AI_ENGINES);
            },
          });
          enginesToRemove.push(engineName);
        } else if (runningFailed) {
          addNotification({
            message: `Engine "${currentEngine.displayName || engineName}" failed to start.`,
            messageBarType: MessageBarType.error,
            title: 'Engine Failed',
            onClick: () => {
              history.push(RoutePaths.MY_AI_ENGINES);
            },
          });
          enginesToRemove.push(engineName);
        } else if (ignoredEngine) {
          enginesToRemove.push(engineName);
        }

        // Update previous state
        if (currentState) {
          previousEngineStates.set(engineName, currentState);
        }
      });

      // Remove engines that have transitioned from tracking
      enginesToRemove.forEach((engineName) => {
        trackedStartingEngines.delete(engineName);
        previousEngineStates.delete(engineName);
      });
    },
    [addNotification, history]
  );

  // Track engines in STARTING state
  const trackEngines = useCallback((engines: Engine[] | undefined) => {
    if (!engines) return;

    engines
      .filter((engine) => engine.state === Engine_State.STARTING && engine.name)
      .forEach((engine) => {
        if (!trackedStartingEngines.has(engine.name!)) {
          trackedStartingEngines.add(engine.name!);
          previousEngineStates.set(engine.name!, Engine_State.STARTING);
        }
      });
  }, []);

  // Compute refetch interval: React Query will handle polling based on query enabled state
  // We always return the interval if provided, and let the query's enabled state control when to poll
  const computedRefetchInterval: number | false = useMemo(() => {
    return refetchInterval ?? false;
  }, [refetchInterval]);

  return {
    trackEngines,
    handleStateTransitions,
    refetchInterval: computedRefetchInterval,
    trackedEnginesCount: trackedStartingEngines.size,
  };
};
